import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApiError } from '@/lib/api';

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) {
    const redirect = (location.state as { from?: { pathname: string } } | null)?.from?.pathname || '/devices';
    return <Navigate to={redirect} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError('Please enter both fields.');
      return;
    }
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate((location.state as { from?: { pathname: string } } | null)?.from?.pathname || '/devices', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Login failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ink via-slate-900 to-slate-800 p-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="w-full max-w-md"
      >
        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl shadow-2xl p-7 space-y-4"
        >
          {/* Full brand lockup has dark text, so it lives on the white card. */}
          <div className="text-center pb-3 mb-1 border-b border-slate-100">
            <h1 className="sr-only">SamvidhX Systems</h1>
            <img src="/brand/logo-full.png" alt="SamvidhX Systems — Automate | Connect | Evolve"
              width={720} height={213} className="mx-auto w-full max-w-[300px] h-auto" draggable={false} />
            <p className="mt-3 text-xs text-slate-500">Solar panel cleaning · remote control</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5" htmlFor="login-user">
              Username
            </label>
            <Input
              id="login-user"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="superadmin"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5" htmlFor="login-pass">
              Password
            </label>
            <Input
              id="login-pass"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="text-sm text-bad bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" loading={submitting}>
            Sign in
          </Button>
        </form>

        <p className="text-center mt-6 text-xs text-white/40">
          © {new Date().getFullYear()} SamvidhX Systems · <a href="/legacy" className="hover:text-white/70">Legacy interface</a>
        </p>
      </motion.div>
    </div>
  );
}
